<template>
    <footer class="footer">
        <hr class="at-least-desktop" />
        <div class="inner footer-links">
            <div class="icons">
                <nuxt-link
                    v-for="icon in icons"
                    :key="icon.id"
                    v-bind="$cms.parseLink(icon)"
                >
                    <DSFrontifyAsset
                        compute-width
                        :asset="icon?.image"
                    />
                </nuxt-link>
            </div>
            <template v-if="computedItems">
                <div
                    v-for="(item, key) in computedItems"
                    :key="item.id"
                    class="links"
                >
                    <h4
                        class="h5 footer-heading"
                        :class="{
                            'no-border': key === 0,
                        }"
                    >
                        <button
                            class="mobile"
                            @click="openMenuItem(item.id)"
                        >
                            <span>{{ item.value }}</span>
                            <DSIcon :code="`st1website-fill/${selected.includes(item.id) ? 'minus' : 'plus'}`" :class="{ 'open': selected.includes(item.id), }" size="l" />
                        </button>
                    </h4>
                    <DSHeightTransition :open="selected.includes(item.id) || $bp.bps.isDesktop">
                        <template v-for="link in item.children" :key="link.id">
                            <nuxt-link
                                class="link"
                                v-bind="$cms.parseLink(link)"
                                text-align="left"
                            >
                                {{ link.title }}
                            </nuxt-link>
                        </template>
                    </DSHeightTransition>
                </div>
            </template>
        </div>
        <div v-if="download.length > 0 || some.length > 0" class="inner downloads">
            <div v-if="download.length > 0" class="download">
                <h5 class="h5">{{ $t('layout.downloadTitle') }}</h5>
                <template v-for="item in downloadOne" :key="item.id">
                    <nuxt-link
                        v-bind="$cms.parseLink(item)"
                    >
                        <DSFrontifyAsset :asset="{ ...item.image[0], size: 'xl' }" />
                    </nuxt-link>
                </template>
            </div>
            <div v-if="download.length > 2" class="download2">
                <h5 class="h5">{{ $t('layout.downloadTitle2') }}</h5>
                <template v-for="item in downloadTwo" :key="item.id">
                    <nuxt-link
                        v-bind="$cms.parseLink(item)"
                    >
                        <DSFrontifyAsset :asset="{ ...item.image[0], size: 'xl' }" />
                    </nuxt-link>
                </template>
            </div>
            <div v-if="computedSome.length > 0" class="some">
                <h5 class="h5">{{ $t('layout.someTitle') }}</h5>
                <nuxt-link
                    v-for="item in computedSome"
                    :key="item.id"
                    v-bind="$cms.parseLink(item)"
                >
                    <DSFrontifyAsset
                        :asset="item.image"
                        class="icon"
                        size="s"
                    />
                </nuxt-link>
            </div>
        </div>
        <div class="copyright-and-links">
            <div class="inner">
                <div class="extras">
                    <slot name="copyright" :current-year="currentYear">
                        <div class="copy"><strong>© {{ props.copyright ?? $t('layout.copyright') }} {{ currentYear }}</strong></div>
                    </slot>
                    <div class="links">
                        <template v-for="item in extras" :key="item.id">
                            <button
                                v-if="item.internal === 'Cookie Policy Trigger'"
                                :key="`cookies-${item.id}`"
                                type="button"
                                @click="openConsent"
                            >
                                {{ item.title }}
                            </button>
                            <nuxt-link
                                v-else
                                v-bind="$cms.parseLink(item)"
                            >
                                {{ item.title }}
                            </nuxt-link>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </footer>
</template>
<script setup>
import { ref, computed } from 'vue';
import { useI18n, useNuxtApp } from '#imports';
const { $bp } = useNuxtApp();
const selected = ref([]);

const props = defineProps({
    icons: {
        type: Array,
        required: false,
        default: () => [],
    },
    items: {
        type: Array,
        required: false,
        default: () => [],
    },
    download: {
        type: Array,
        required: false,
        default: () => [],
    },
    some: {
        type: Array,
        required: false,
        default: () => [],
    },
    extras: {
        type: Array,
        required: false,
        default: () => [],
    },
    copyright: {
        type: String,
        required: false,
        default: null,
    },
});

const currentYear = computed(() => (new Date()).getFullYear());

const downloadOne = computed(() => ([...props.download.slice(0, 2)]));
const downloadTwo = computed(() => ([...props.download.slice(2, 4)]));
const computedSome = computed(() => props.some.filter(some => some.url));

const computedItems = computed(() => {
    const parsedItems = [];
    let currentItem = null;

    props.items.forEach((item, key) => {
        if (item.contentType === 'string') {
            if (currentItem !== null) {
                parsedItems.push(currentItem);
            }
            currentItem = JSON.parse(JSON.stringify(item));
        } else {
            if (!currentItem.children) {
                currentItem.children = [];
            }
            currentItem.children.push(item);
            if (key === props.items.length - 1) {
                parsedItems.push(currentItem);
            }
        }
    });

    return parsedItems;
});

const openMenuItem = value => {
    if (selected.value.includes(value)) {
        selected.value.splice(selected.value.indexOf(value), 1);
    } else {
        selected.value.push(value);
    }
};

const openConsent = () => {
    window?.CookieConsent?.show();
};

useI18n({
    useScope: 'global',
    messages: {
        'sv-SE': {
            layout: {
                copyright: 'St1',
                downloadTitle: 'Ladda ner appen St1 Mobility',
                downloadTitle2: 'Ladda ner St1 Business appen',
                someTitle: 'Följ oss',
            },
        },
        'fi-FI': {
            layout: {
                copyright: 'St1',
                downloadTitle: 'Lataa St1 Way -sovellus',
                downloadTitle2: 'Lataa St1 Business -sovellus',
                someTitle: 'Seuraa meitä',
            },
        },
        'en-EU': {
            layout: {
                copyright: 'St1',
                downloadTitle: 'Download St1 Way app',
                downloadTitle2: 'Download St1 Business',
                someTitle: 'Follow us on social media',
            },
        },
        'no-NO': {
            layout: {
                copyright: 'St1',
                downloadTitle: 'Last ned Snarveien -appen',
                someTitle: 'Følg oss på sosiale medier',
            },
        },
    },
});
</script>
<style>
footer {
  color: var(--color-base-white);
  background: var(--color-primary-500);
}
footer > hr:first-child {
  margin-top: 0;
}
footer .inner {
  display: grid;
  grid-auto-columns: 1fr;
  grid-gap: 1.75rem 1.5rem;
  width: 100%;
  max-width: var(--page-max-width);
  padding: 3.125rem 0 2rem;
  margin: 0 auto;
}
footer .inner > .links {
  grid-row-start: 2;
}
footer .inner > .links h4 button {
  width: 100%;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
}
@media (min-width: 1200px) {
  footer .inner > .links h4 button {
    cursor: default;
  }
  footer .inner > .links h4 button .icon {
    display: none;
  }
}
@media (max-width: 1199px) {
  footer .inner > .links h4 {
    margin-bottom: 0;
    padding: 0.75rem 0;
  }
  footer .inner > .links h4:not(.no-border) {
    border-top: 1px solid var(--color-base-white);
  }
}
footer .inner > .links h4 .icon {
  margin: 0 1.125rem 0 0.5rem;
  transition: transform 0.5s ease-in-out;
}
footer .inner > .links .link {
  display: flex;
  padding: 0.5rem 0;
  text-decoration: none;
  color: var(--color-base-white);
}
footer .inner > .links .link:visited {
  color: var(--color-base-white);
}
@media (max-width: 1199px) {
  footer .inner > .links .link:last-child {
    margin-bottom: 0.75rem;
  }
}
@media (min-width: 1200px) {
  footer .inner > .links .height-transition {
    grid-template-rows: 1fr;
  }
  footer .inner > .links .height-transition > .container {
    opacity: 1;
  }
}
@media (max-width: 1199px) {
  footer .inner {
    display: flex;
    flex-flow: column wrap;
    justify-content: flex-start;
    grid-gap: 0;
    padding-right: 1rem;
    padding-left: 1rem;
  }
}
footer hr {
  margin-top: 1rem;
  position: relative;
  width: 100%;
  grid-column-start: 1;
  grid-column-end: -1;
}
footer hr.at-least-desktop {
  display: none;
}
@media (min-width: 1200px) {
  footer hr.at-least-desktop {
    display: block;
  }
}
footer .download,
footer .download2 {
  grid-column-end: span 2;
}
footer .download a:not(:last-child),
footer .download2 a:not(:last-child) {
  margin-right: 1rem;
}
footer .download a .image-container,
footer .download2 a .image-container {
  width: 160px;
  display: inline-flex;
  height: 45px;
}
footer .download2 {
  grid-column-start: 3;
  grid-column-end: 5;
}
footer .some a:not(:last-child) {
  margin-right: 1.5rem;
}
footer .some a .icon {
  color: var(--color-secondary-500);
}
footer .some a:hover {
  background: var(--color-secondary-100);
}
footer .extras {
  grid-column-end: span 5;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
}
footer .extras .copy {
  order: 1;
}
@media (max-width: 1199px) {
  footer .extras {
    grid-column-end: 1;
    flex-flow: column wrap;
    justify-content: center;
    align-items: center;
  }
  footer .extras .copy {
    margin-top: 1rem;
  }
}
footer .extras .links {
  display: inline-flex;
  align-items: start;
  width: auto;
  flex-grow: 1;
  margin-right: 1rem;
}
footer .extras .links a {
  display: inline-flex;
  text-decoration: none;
  color: var(--color-base-white);
}
footer .extras .links a:visited {
  color: var(--color-base-white);
}
@media (max-width: 1199px) {
  footer .extras .links a {
    margin-bottom: 0.5rem;
  }
}
@media (min-width: 1200px) {
  footer .extras .links a:not(:first-child) {
    margin-left: 1.5rem;
  }
}
footer .extras .links :nth-child(1)::after,
footer .extras .links :nth-child(2)::after {
  content: "|";
  padding-left: 1.5rem;
}
footer .extras .links :nth-child(3) {
  margin: 0 auto 0.5rem;
}
@media (max-width: 1199px) {
  footer .extras .links {
    padding-left: 0;
    display: flex;
    flex-flow: column wrap;
    justify-content: center;
    align-items: center;
  }
}
footer .copyright-and-links .inner {
  padding: 2rem 0;
}
@media (max-width: 991px) {
  footer .download, footer .extras, footer .some {
    margin-top: 2rem;
  }
}

.icons {
  grid-column-start: 1;
  grid-column-end: span 5;
  grid-row-start: 1;
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  margin-block: 1rem;
  align-items: center;
}
.icons a {
  display: inline-flex;
}
.icons a:not(:last-child) {
  margin-right: 4.25rem;
}
.icons a .image-container {
  height: 75px;
}
@media (max-width: 1199px) {
  .icons a:not(:last-child) {
    margin-right: 2rem;
  }
  .icons a .image-container {
    height: 32px;
  }
}

.ds-modal-content.terms {
  max-width: 960px;
}
.ds-modal-content.terms .body {
  padding: 1.5rem;
}
.ds-modal-content.terms .foot {
  padding-right: 1rem;
}
</style>
